import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BookOpenText, CalendarClock, Compass, Heart, FileText, Building2, UserCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useUiText } from "@/contexts/UiTextContext";
import { ScriptureVerse } from "@/components/patria/ScriptureVerse";
import { ModuleCard } from "@/components/patria/ModuleCard";
import NextOneOnOneCard from "@/components/rhythm/NextOneOnOneCard";
import FaithJourneyCard from "@/components/faith_journey/FaithJourneyCard";
import api from "@/lib/api";

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good Morning";
  if (h < 17) return "Good Afternoon";
  return "Good Evening";
}

const MODULES = [
  {
    key: "lift",
    title: "LIFT",
    tagline: "Evaluations",
    description:
      "A gentle rhythm of check-ins and evaluations that lift the whole team.",
    icon: BookOpenText,
    accent: "green",
  },
  {
    key: "compass",
    title: "COMPASS",
    tagline: "Growth",
    description:
      "Direction, goals, and coaching conversations that keep everyone pointed north.",
    icon: Compass,
    accent: "terra",
  },
  {
    key: "rhythm",
    title: "RHYTHM",
    tagline: "1:1 Tracking & Development",
    description:
      "A warm cadence of shepherded 1:1s — shared agendas, honest notes, and steady growth plans.",
    icon: CalendarClock,
    accent: "green",
  },
  {
    key: "er-center",
    title: "ER Center",
    tagline: "Employee Relations",
    description:
      "A safe, confidential place to raise concerns and receive pastoral, professional support.",
    icon: Heart,
    accent: "green",
  },
  {
    key: "training",
    title: "Training",
    tagline: "Learning",
    description:
      "Onboarding, safety, and formation content — all in one shepherded library.",
    icon: FileText,
    accent: "terra",
  },
  {
    key: "resources",
    title: "RESOURCES",
    tagline: "Shared Library",
    description:
      "Playbooks, forms, guides, and helpful links — curated by People Operations for the whole team.",
    icon: BookOpenText,
    accent: "green",
  },
];

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useUiText();
  const navigate = useNavigate();
  const [notifs, setNotifs] = useState([]);
  const [campusName, setCampusName] = useState(null);
  const [managerName, setManagerName] = useState(null);
  const firstName = useMemo(() => (user?.name || "").split(" ")[0], [user]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [n, campuses] = await Promise.all([
          api.get("/notifications", { params: { limit: 5 } }),
          api.get("/campuses"),
        ]);
        if (!mounted) return;
        setNotifs(n.data);
        if (user?.campus_id) {
          const c = campuses.data.find((cc) => cc.id === user.campus_id);
          setCampusName(c?.name || null);
        }
      } catch {
        /* ignore */
      }
      if (user?.manager_id) {
        try {
          const { data } = await api.get(`/users/${user.manager_id}`);
          if (mounted) setManagerName(data?.name || null);
        } catch {
          /* ignore */
        }
      }
    })();
    return () => {
      mounted = false;
    };
  }, [user]);

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <section>
        <p className="text-xs uppercase tracking-widest text-terracotta-500 font-medium">
          Your myPatria
        </p>
        <h1
          className="mt-1 text-4xl sm:text-5xl font-serif text-forest-900"
          data-testid="dashboard-greeting"
        >
          {(t("dashboard.greeting_template", "Good Morning, {first_name}")
              .replace("{first_name}", firstName || "friend"))}
        </h1>
        <p className="mt-2 text-charcoal-soft max-w-2xl" data-testid="dashboard-subtitle">
          {t("dashboard.subtitle", "Your Warm Snapshot Of Life At Patria.")}
        </p>
      </section>

      <ScriptureVerse />

      {user?.role && user.role !== "volunteer" && <NextOneOnOneCard />}
      <FaithJourneyCard />

      <section>
        <h2 className="text-2xl font-serif text-forest-900 mb-4" data-testid="dashboard-modules-heading">
          {t("dashboard.modules_heading", "Modules")}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
          {MODULES.map((m) => {
            const { key: mKey, ...rest } = m;
            const active = mKey === "lift" || mKey === "compass" || mKey === "rhythm" || mKey === "er-center" || mKey === "training" || mKey === "resources";
            const targets = { lift: "/lift", compass: "/compass", rhythm: "/rhythm", "er-center": "/er", training: "/training", resources: "/resources" };
            return (
              <ModuleCard
                key={mKey}
                moduleKey={mKey}
                {...rest}
                comingSoon={!active}
                onClick={active ? () => navigate(targets[mKey]) : undefined}
              />
            );
          })}
        </div>
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Notifications */}
        <div className="patria-card p-6 lg:col-span-2" data-testid="dashboard-recent-notifs">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-serif text-forest-900">Recent Notifications</h3>
            <Link to="/notifications" className="patria-link text-sm">
              View all
            </Link>
          </div>
          <ul className="mt-4 divide-y divide-sage-300/60">
            {notifs.length === 0 && (
              <li className="py-6 text-sm text-charcoal-muted italic">
                You&rsquo;re all caught up.
              </li>
            )}
            {notifs.map((n) => (
              <li key={n.id} className="py-3 flex items-start gap-3">
                <span
                  className={`mt-1.5 h-2 w-2 rounded-full ${
                    n.read ? "bg-sage-300" : "bg-terracotta-500"
                  }`}
                  aria-hidden
                />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-forest-900">{n.title}</p>
                  <p className="text-sm text-charcoal-soft line-clamp-2">{n.body}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* Profile summary */}
        <div className="patria-card p-6" data-testid="dashboard-profile-summary">
          <h3 className="text-xl font-serif text-forest-900">Your Details</h3>
          <dl className="mt-4 space-y-3 text-sm">
            <div className="flex items-start gap-3">
              <UserCircle className="h-4 w-4 mt-0.5 text-forest-600" strokeWidth={1.5} />
              <div>
                <dt className="text-xs uppercase tracking-widest text-charcoal-muted">Role</dt>
                <dd className="font-medium text-forest-900 capitalize">{user?.role?.replace("_", " ")}</dd>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Building2 className="h-4 w-4 mt-0.5 text-forest-600" strokeWidth={1.5} />
              <div>
                <dt className="text-xs uppercase tracking-widest text-charcoal-muted">Campus</dt>
                <dd className="font-medium text-forest-900">{campusName || "—"}</dd>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <FileText className="h-4 w-4 mt-0.5 text-forest-600" strokeWidth={1.5} />
              <div>
                <dt className="text-xs uppercase tracking-widest text-charcoal-muted">Department</dt>
                <dd className="font-medium text-forest-900">{user?.department || "—"}</dd>
              </div>
            </div>
            {managerName && (
              <div className="flex items-start gap-3">
                <UserCircle className="h-4 w-4 mt-0.5 text-forest-600" strokeWidth={1.5} />
                <div>
                  <dt className="text-xs uppercase tracking-widest text-charcoal-muted">Manager</dt>
                  <dd className="font-medium text-forest-900">{managerName}</dd>
                </div>
              </div>
            )}
          </dl>
          <button
            onClick={() => navigate("/profile")}
            className="patria-btn-secondary mt-6 w-full"
            data-testid="dashboard-open-profile"
          >
            Open my profile
          </button>
        </div>
      </section>
    </div>
  );
}
